import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
import Button from '../common/Button/Button';
import InputField from '../common/InputField/InputField';
import { Invoice, InvoiceItem, InvoiceStatus } from '../../types/invoiceTypes';
import { Customer } from '../../types/customerTypes';
import { formatCurrency } from '../../utils/helpers';
import styles from './InvoiceForm.module.scss';
// import { useAuth } from '../../hooks/useAuth';
import customerService from '../../services/customerService';
import accountHeadService from '../../services/accountHeadService';
import invoiceService from '../../services/invoiceService';

interface InvoiceFormProps {
  invoice?: Invoice;
  existingInvoices?: Invoice[];
  onSubmit: (invoice: Invoice) => void;
  onCancel: () => void;
  loading?: boolean;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({
  invoice,
  existingInvoices = [],
  onSubmit,
  onCancel,
  loading = false
}) => {
  // const navigate = useNavigate();
  // const { auth } = useAuth();
   const getCurrentTimeString = () => {
    const now = new Date();
    let hours = now.getHours();
    const minutes = now.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12; // Convert 0 -> 12
    const strMinutes = minutes < 10 ? `0${minutes}` : minutes;
    return `(Time - ${hours}:${strMinutes} ${ampm})`;
  };
  const [formData, setFormData] = useState<Partial<Invoice>>({
    invoice_number: '',
    customer_id: '',
    customer_name: '',
    invoice_date: new Date().toLocaleDateString('en-CA'),
    due_date: '',
    items: [{ id: '1', description: ``, amount: 0, service_charge: 0, account_head_id: 0, account_head_name: '' }],
    total_amount: 0,
    status: 'pending' as InvoiceStatus,
    notes: '',
    partially_received_amounts: 0,
  });
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [accountHeads, setAccountHeads] = useState<any[]>([]);
  const [showDescription, setShowDescription] = useState(false);

  // Generate invoice number when form loads or the invoice date changes (only for new invoices)
  useEffect(() => {
    const generateInvoiceNumber = async () => {
      if (!invoice) {
        try {
          const response = await invoiceService.generateInvoiceNumber(formData.invoice_date);
          setFormData(prev => ({ ...prev, invoice_number: response.invoice_number }));
        } catch (error) {
          console.error('Failed to generate invoice number:', error);
          // Fallback to a simple timestamp-based number
          const fallbackNumber = `INV${Date.now().toString().slice(-6)}`;
          setFormData(prev => ({ ...prev, invoice_number: fallbackNumber }));
        }
      }
    };

    generateInvoiceNumber();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice, formData.invoice_date]);

  // Handle invoice data and due date generation
  useEffect(() => {
    if (invoice) {
      const date = new Date(invoice.invoice_date);
      date.setDate(date.getDate() + 1);
      const due_date = new Date(invoice.due_date);
      due_date.setDate(due_date.getDate() + 1);
      setFormData({
        ...invoice,
        items: invoice.items.map(item => ({
          ...item,
          service_charge: Number(item.service_charge) || 0,
          amount: Number(item.amount) || 0,
        })),
      invoice_date: date.toLocaleDateString('en-CA'),
      due_date: due_date.toLocaleDateString('en-CA')
      });
    } else {
      // Set due date for new invoices
      const due_date = generateDueDate(formData.invoice_date || new Date().toLocaleDateString('en-CA'));
      setFormData(prev => ({ ...prev, due_date }));
    }
  }, [formData.invoice_date, invoice]);

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const data = await customerService.getCustomers();
        setCustomers(Array.isArray(data) ? data : []);
      } catch (error) {
        setCustomers([]);
      } finally {
        setLoadingCustomers(false);
      }
    };
    fetchCustomers();
  }, []);

  useEffect(() => {
    const fetchAccountHeads = async () => {
      // Use your accountHeadService or fetch directly
      const data = await accountHeadService.getAccountHeads();
      const activeAccountHeads = Array.isArray(data) ? data.filter(ah => ah.is_active) : [];
      setAccountHeads(activeAccountHeads);
    };
    fetchAccountHeads();
  }, []);

  // Update due date when invoice date changes
  useEffect(() => {
    if (!invoice && formData.invoice_date) {
      const due_date = generateDueDate(formData.invoice_date);
      setFormData(prev => ({ ...prev, due_date }));
    }
  }, [formData.invoice_date, invoice]);

  useEffect(() => {
    const selectedCustomer = customers.find(c => String(c.id) === String(formData.customer_id));
    setShowDescription(selectedCustomer?.is_des === true);
  }, [formData.customer_id, customers]);

  const handleInputChange = (field: keyof Invoice, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomerChange = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    setFormData(prev => ({
      ...prev,
      customer_id: customerId,
      customer_name: customer?.name || ''
    }));
  };

  const handleAccountHead = (index: number, field: keyof InvoiceItem, value: any) => {
    const account_head = accountHeads.find(c => c.id === value);
    
    const newItems = [...(formData.items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    newItems[index] = { ...newItems[index], account_head_name: account_head?.name };

    setFormData(prev => ({
      ...prev,
      items: newItems,
    }));
  };

  // const handleAccountDesc = (index: number, field: keyof InvoiceItem, value: any) => {
  //   const account_head = accountHeads.find(c => c.id === value);
    
  //   const newItems = [...(formData.items || [])];
  //   newItems[index] = { ...newItems[index], [field]: value };
  //   newItems[index] = { ...newItems[index], account_head_name: account_head?.name };

  //   setFormData(prev => ({
  //     ...prev,
  //     items: newItems,
  //   }));
  // };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: any) => {
    const newItems = [...(formData.items || [])];
  newItems[index] = { ...newItems[index], [field]: value };
    
    // Recalculate total (amount + service_charge for each item)
    const total_amount = newItems.reduce((sum, item) => {
      const amt = Number(item.amount) || 0;
      const sc = Number(item.service_charge) || 0;
      return sum + amt + sc;
    }, 0);
    
    setFormData(prev => ({
      ...prev,
      items: newItems,
      total_amount
    }));
  };

  const addItem = () => {
    const newItem: InvoiceItem = {
      id: Date.now().toString(),
      description: ``,
      amount: 0,
      service_charge: 0,
      account_head_id: 0,
      account_head_name: ''
    };
    setFormData(prev => ({
      ...prev,
      items: [...(prev.items || []), newItem]
    }));
  };

  const removeItem = (index: number) => {
    if ((formData.items?.length || 0) > 1) {
      const newItems = formData.items?.filter((_, i) => i !== index) || [];
  
      // Calculate total from updated list
      const total_amount = newItems.reduce((sum, item) => {
        const amt = Number(item.amount) || 0;
        const sc = Number(item.service_charge) || 0;
        return sum + amt + sc;
      }, 0);
  
      setFormData(prev => ({
        ...prev,
        items: newItems,
        total_amount
      }));
    }
  };
  

  const handleSubmit = (e: React.FormEvent) => {
  e.preventDefault();

  if (!formData.total_amount || formData.total_amount === 0) {
    alert('Please enter at least one item with a non-zero amount or service charge.');
    return;
  }

  const itemsWithTime = (formData.items || []).map(item => {
    // Check if description already contains a timestamp
    const timeRegex = /\(Time - .*?\)/;
    if (timeRegex.test(item.description)) {
      // Already has time — keep as is
      return item;
    } else {
      // No time, append current time
      const baseDescription = item.description.trim();
      return {
        ...item,
        description: `${baseDescription} ${getCurrentTimeString()}`
      };
    }
  });

  const invoiceToSubmit = {
    ...formData,
    items: itemsWithTime,
  };

  onSubmit(invoiceToSubmit as Invoice);
};


  return (
    <form onSubmit={handleSubmit} className={styles.invoiceForm}>
      <div className={styles.formGrid}>
        <div className={styles.formSection}>
          <h2>Invoice Details</h2>
          <div className={styles.formRow}>
            <div className={styles.selectWrapper}>
              <label htmlFor="customer" className={styles.label}>Customer</label>
              <select
                id="customer"
                value={formData.customer_id || ''}
                onChange={(e) => handleCustomerChange(e.target.value)}
                className={styles.select}
                required
                disabled={loadingCustomers}
              >
                <option value="">Select Customer</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>
{/*
            <div className={styles.selectWrapper}>
              <label htmlFor="status" className={styles.label}>Status</label>
              <select
                id="status"
                value={formData.status || 'pending'}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className={styles.select}
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="partially_paid">Partially Paid</option>
              </select>
            </div> */}
          </div>
          <div className={styles.formRow}>
            {showDescription && (
              <InputField
                name="notes"
                label="Description"
                value={formData.notes || ''}
                onChange={e => handleInputChange('notes', e.target.value)}
                required
              />
            )}
 {formData.status === 'partially_paid' && (
              <InputField
                type="number"
                name="partially_received_amounts"
                label="Partially Paid Amount"
                value={formData.partially_received_amounts || ''}
                onChange={e => handleInputChange('partially_received_amounts', parseFloat(e.target.value) || 0)}
                required
              />
            )}
          </div>
          
        </div>

        <div className={styles.formSection}>
          <h2>Invoice Items</h2>
          <div className={styles.itemsContainer}>
            {formData.items?.map((item, index) => (
              <div key={item.id} className={styles.itemRow}>
                <div style={{ width: 280, marginTop: '1rem' }}>
  <InputField
    name={`description-${index}`}
    label="Description"
    value={item.description}
    onChange={(e) => handleItemChange(index, 'description', e.target.value)}
  />
</div>

                <div className={styles.selectWrapper} style={{ minWidth: 280 }}>
                  <label htmlFor={`accountHead-${index}`} className={styles.label}>Account Head</label>
                  <select
                    id={`accountHead-${index}`}
                    value={item.account_head_id || ''}
                    onChange={e => handleAccountHead(index, 'account_head_id', e.target.value)}
                    className={`${styles.select} ${styles.accountHeadSelect}`}
                    required
                  >
                    <option value="">Select Account Head</option>
                    {accountHeads.map(ah => (
                      <option key={ah.id} value={ah.id}>{ah.name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ width: 280 }}>
                <InputField
                  type="number"
                  name={`amount-${index}`}
                  label="Amount"
                  value={item.amount}
                  onChange={(e) => handleItemChange(index, 'amount', parseFloat(e.target.value) || 0)}
                  required
                />
                </div>
                <div style={{ width: 280 }}>
                <InputField
                  type="number"
                  name={`service_charge-${index}`}
                  label="S/C (Service Fee)"
                  value={item.service_charge ?? 0}
                  onChange={e => handleItemChange(index, 'service_charge', parseFloat(e.target.value) || 0)}
                  placeholder="Enter Service Fee"
                />
                </div>
                {formData.items && formData.items.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className={styles.removeItem}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <Button
                type="button"
                variant="secondary"
                onClick={addItem}
                className={styles.addItemButton}
              >
                + Add Item
              </Button>
            </div>
          </div>
        </div>
        <div className={styles.totalsSection}>
          <div className={`${styles.totalRow} ${styles.grandTotal}`}>
            <span>Total Amount:</span>
            <span>{formatCurrency(formData.total_amount || 0)}</span>
          </div>
        </div>
      </div>

      <div className={styles.formActions}>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : (invoice ? 'Update Invoice' : 'Create Invoice')}
        </Button>
      </div>
    </form>
  );
};

// Generate due date (30 days from invoice date)
const generateDueDate = (invoiceDate: string): string => {
  const date = new Date(invoiceDate);
  date.setDate(date.getDate() + 30);
  return date.toLocaleDateString('en-CA');
};

export default InvoiceForm; 
